import { useState } from "react";

import type { DraftBatchItem } from "@/api/types";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

/**
 * Top-bar bulk-action buttons: "Approve all pending" and "Reject all
 * pending". "Pending" = items not already decided in `audit_log` AND not
 * yet decided in the current session (`localDecisions`).
 *
 * Approve-all opens a confirm dialog when any pending row has
 * `validator_flag === true` — the safety guardrail against an over-
 * eager click that would promote questionable rows. Reject-all has no
 * confirm: rejecting a flagged row is the safe direction.
 */
export interface BulkActionsProps {
  items: ReadonlyArray<DraftBatchItem>;
  /** In-session decisions, keyed by `draftId`. Read-only here. */
  localDecisions: ReadonlyMap<string, "approved" | "rejected">;
  /** Apply `decision` to every pending row in one shot. */
  onApplyAll: (decision: "approved" | "rejected") => void;
}

export function BulkActions({ items, localDecisions, onApplyAll }: BulkActionsProps) {
  const pending = items.filter(
    (i) => i.existingDecision === null && !localDecisions.has(i.draftId),
  );
  const flaggedPending = pending.filter((i) => i.validatorFlag === true);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const noPending = pending.length === 0;

  const handleApproveAll = () => {
    if (flaggedPending.length > 0) {
      setConfirmOpen(true);
    } else {
      onApplyAll("approved");
    }
  };

  const handleConfirmApprove = () => {
    setConfirmOpen(false);
    onApplyAll("approved");
  };

  return (
    <>
      <div className="flex items-center gap-2">
        <Button
          variant="outline"
          size="sm"
          disabled={noPending}
          onClick={() => onApplyAll("rejected")}
        >
          Reject all pending ({pending.length})
        </Button>
        <Button
          variant="default"
          size="sm"
          disabled={noPending}
          onClick={handleApproveAll}
        >
          Approve all pending ({pending.length})
        </Button>
      </div>

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Approve flagged items?</DialogTitle>
            <DialogDescription>
              {flaggedPending.length} of {pending.length} pending items were
              flagged by the validator. Approving them now will promote them
              to <span className="font-mono">tools_and_services</span>.
            </DialogDescription>
          </DialogHeader>
          <ul className="max-h-40 list-disc space-y-1 overflow-y-auto pl-6 text-sm text-text">
            {flaggedPending.map((item) => (
              <li key={item.draftId}>
                <span className="font-mono text-xs text-text-muted">{item.draftId}</span>{" "}
                — {item.name}
              </li>
            ))}
          </ul>
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setConfirmOpen(false)}>
              Cancel
            </Button>
            <Button variant="default" size="sm" onClick={handleConfirmApprove}>
              Approve all anyway
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
